import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { CustomCotationsOtherFormData } from "@/types/forms/home/customCotationsOtherForm.form";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import ShipmentTransitIcon from "@/assets/icons/shipment-transit.svg?react";
import ShieldIcon from "@/assets/icons/shield.svg?react";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import AttachmentIcon from "@/assets/icons/attachment.svg?react";
import CommentIcon from "@/assets/icons/comment.svg?react";
import { ExpeditionDocumentFileDto } from "@/types/api/api-types";
import {
  ExpeditionDocumentCard,
  NewFileDocumentCard,
} from "../backOffice/expeditionForm/expeditionDocumentCard";
import { Plus } from "lucide-react";
import { Button } from "../ui/button";
import { UploadFileClientModal } from "../backOffice/expeditionForm/uploadFileModal";
import { useState } from "react";
import {
  FileAction,
  getCreatableFileTypes,
  getVisibleFileTypes,
  sortDocumentsByPriority,
} from "@/constants/fileCrud";
import { useOtherCustomQuotationTypeOptions } from "@/hooks/useEnumOptions";

type Props = {
  documents: ExpeditionDocumentFileDto[];
};

export const InfoProductOtherType = ({ documents }: Props) => {
  const { t } = useTranslation("boQuotations");
  const otherCustomQuotationOptions = useOtherCustomQuotationTypeOptions();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [newFiles, setNewFiles] = useState<{ fileId: string; file: File; type: FileTypeEnum }[]>(
    [],
  );
  const methods = useFormContext<CustomCotationsOtherFormData>();

  const newFilesId = methods.watch("newFilesId");
  const removedFilesId = methods.watch("removedFilesId");
  const readableFileTypes = getVisibleFileTypes(FileAction.FO_CREATE_EXPEDITION);
  const creatableFileTypes = getCreatableFileTypes(FileAction.FO_CREATE_EXPEDITION);
  const readableDocuments = sortDocumentsByPriority(
    documents.filter((document) => readableFileTypes.includes(document.type as FileTypeEnum)),
  );

  const handleDeleteFile = async (fileId: string) => {
    const isNewlyAdded = newFilesId?.includes(fileId);
    const isExisting = documents?.some((document) => document.id === fileId);

    if (isNewlyAdded) {
      methods.setValue("newFilesId", newFilesId?.filter((id) => id !== fileId) || []);
      setNewFiles(newFiles.filter((file) => file.fileId !== fileId));
    } else if (isExisting) {
      methods.setValue("removedFilesId", [...new Set([...(removedFilesId || []), fileId])]);
    }
  };

  const handleUploadSuccess = (fileId: string, selectedType: string, file: File) => {
    setNewFiles([...newFiles, { fileId, file, type: selectedType as FileTypeEnum }]);
    methods.setValue("newFilesId", [...(newFilesId || []), fileId]);
  };

  return (
    <div className="flex w-full flex-col items-center justify-center gap-6">
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <div className="flex w-full flex-col items-center justify-center gap-2">
            <h2 className="text-brand700 text-xl font-semibold">{t("productOther.title")}</h2>
          </div>
          <div className="flex w-full flex-col gap-2">
            <ControlledSelectV2
              control={methods.control}
              name="otherCustomQuotationType"
              options={otherCustomQuotationOptions}
              placeholder={t("productOther.typePlaceholder")}
            />
            <InfoToDisplay otherCustomQuotationType={methods.watch("otherCustomQuotationType")} />
          </div>
        </div>
      </div>
      <div className="flex w-full max-w-[800px] flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <CommentIcon />
          <h2 className="text-xm font-semibold text-black">{t("productOther.commentTitle")}</h2>
        </div>
        <textarea
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-normal hover:bg-gray-50"
          rows={4}
          cols={50}
          placeholder="..."
          {...methods.register("comment")}
        />
      </div>
      <div className="flex w-full max-w-[800px] flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <AttachmentIcon />
          <h2 className="text-xm font-semibold text-black">
            <Trans
              t={t}
              i18nKey="productOther.attachmentsTitle"
              components={{ hint: <span className="text-sm font-normal text-gray-500" /> }}
            />
          </h2>
        </div>
        <div className="flex max-h-[70vh] w-full flex-col gap-8 md:flex">
          <div className="flex flex-col gap-2">
            {readableDocuments
              ?.filter((file: ExpeditionDocumentFileDto) => !removedFilesId?.includes(file.id))
              .map((file: ExpeditionDocumentFileDto) => (
                <ExpeditionDocumentCard
                  key={file.id}
                  file={file}
                  onDelete={handleDeleteFile}
                  hideDetailsInfo={true}
                />
              ))}
            {newFiles.map((file) => (
              <NewFileDocumentCard
                key={file.fileId}
                file={file}
                onDelete={handleDeleteFile}
                hideDetailsInfo={true}
              />
            ))}
          </div>

          <Button
            variant="outline"
            type="button"
            className="self-start"
            onClick={() => setUploadModalOpen(true)}
          >
            <Plus className="mr-2 size-4" />
            {t("productOther.importFile")}
          </Button>

          <UploadFileClientModal
            open={uploadModalOpen}
            onOpenChange={setUploadModalOpen}
            allowedFileTypes={creatableFileTypes}
            uploadSuccessCallback={handleUploadSuccess}
          />
        </div>
      </div>
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-1 flex-row items-center justify-between gap-4">
          <div className="flex flex-1 flex-row items-center justify-between gap-4">
            <ShieldIcon className="text-zinc-900" />
            <div className="flex flex-1 flex-col">
              <p className="font-semibold">{t("productOther.insuranceTitle")}</p>
              <p className="hidden md:block">{t("productOther.insuranceDescription")}</p>
            </div>
          </div>
          <StyledSwitch name="insurance" control={methods.control} />
        </div>
        <p className="block w-full md:hidden">{t("productOther.insuranceDescription")}</p>
      </div>
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-1 flex-row items-center justify-between gap-4">
          <ShipmentTransitIcon className="text-zinc-900" />
          <div className="flex flex-1 flex-col">
            <p className="font-semibold">{t("productOther.expressTitle")}</p>
            <p className="hidden md:block">{t("productOther.expressDescription")}</p>
          </div>
          <StyledSwitch name="express" control={methods.control} />
        </div>
        <p className="block md:hidden">{t("productOther.expressDescription")}</p>
      </div>
    </div>
  );
};

const InfoToDisplay = ({
  otherCustomQuotationType,
}: {
  otherCustomQuotationType: OtherCustomQuotationTypeEnum;
}) => {
  const { t } = useTranslation("boQuotations");
  const info = {
    [OtherCustomQuotationTypeEnum.RFSE]: t("productOther.rfseInfo"),
  };
  if (otherCustomQuotationType === OtherCustomQuotationTypeEnum.RFSE) {
    return (
      <div className="w-full rounded-lg border border-yellow-600 bg-yellow-50 p-4">
        <h3 className="text-base font-semibold text-gray-900">{info[otherCustomQuotationType]}</h3>
      </div>
    );
  }

  return null;
};
